import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  GitMerge, 
  CheckCircle2, 
  RefreshCw,
  Search,
  User,
  Calendar,
  Anchor
} from 'lucide-react';
import toast from 'react-hot-toast';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';
import { alignCruiseToOfficial, ignoreCruiseReconciliation } from '../services/adminService';
import { Cruise } from '../context/CruiseContext';
import { Itinerary } from '../types/itinerary';

interface UnreconciledCruise {
  userCruise: Cruise;
  officialItinerary: Itinerary;
  userName: string;
  userEmail: string;
  linkStatus: 'linked' | 'user_unlinked' | 'never_linked';
  diffs: {
    departurePortMismatch: boolean;
    returnDateMismatch: boolean;
    portsMismatch: boolean;
    hasCustomExcursions: boolean;
    customExcursionsCount: number;
    hasCustomPorts: boolean;
    customPortsCount: number;
    hasCustomBookingInfo: boolean;
  };
}

const AdminItineraryReconciliation = () => {
  const { cruiseLines } = useCruiseMetadata();
  
  // Database States
  const [unreconciled, setUnreconciled] = useState<UnreconciledCruise[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  
  // Filter States
  const [selectedCruiseLine, setSelectedCruiseLine] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [diffFilter, setDiffFilter] = useState<'all' | 'ports' | 'depPort' | 'retDate'>('all');
  const [linkFilter, setLinkFilter] = useState<'all' | 'linked' | 'user_unlinked' | 'never_linked'>('all');

  const scanCruises = async () => {
    setLoading(true);
    try {
      // 1. Fetch Users, Cruises, and Official Itineraries
      const [usersSnap, cruisesSnap, itinerariesSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'cruises')),
        getDocs(collection(db, 'itineraries'))
      ]);

      const usersMap = new Map<string, { name: string; email: string }>();
      usersSnap.docs.forEach(doc => {
        const data = doc.data();
        usersMap.set(doc.id, {
          name: data.preferredDisplayName || data.displayName || 'Unknown User',
          email: data.email || data.consentData?.email || 'No Email'
        });
      });

      // Map of official itineraries keyed by: ${cruiseLine}_${shipName}_${sailingDate} mapping to arrays of Itineraries
      const itinerariesMap = new Map<string, Itinerary[]>();
      itinerariesSnap.docs.forEach(doc => {
        const data = doc.data() as Itinerary;
        const key = `${data.cruiseLine.toLowerCase().trim()}_${data.shipName.toLowerCase().trim()}_${data.sailingDate}`;
        if (!itinerariesMap.has(key)) {
          itinerariesMap.set(key, []);
        }
        itinerariesMap.get(key)!.push(data);
      });

      const unalignedList: UnreconciledCruise[] = [];

      cruisesSnap.docs.forEach(docSnap => {
        const userCruise = docSnap.data() as Cruise;
        userCruise.id = docSnap.id;
        
        if ((userCruise as any).reconciliationIgnored === true) {
          return;
        }
        
        const clKey = userCruise.cruiseLine?.toLowerCase().trim() || '';
        const shipKey = userCruise.shipName?.toLowerCase().trim() || '';
        const dateKey = userCruise.departureDate || '';
        const matchKey = `${clKey}_${shipKey}_${dateKey}`;

        const candidates = itinerariesMap.get(matchKey) || [];
        
        if (candidates.length > 0) {
          // Look for an exact match on return date first
          const exactMatch = candidates.find(it => it.returnDate === userCruise.returnDate);
          
          // Use exact match if found, otherwise fall back to the first candidate
          const officialItinerary = exactMatch || candidates[0];
          // Compare fields
          const departurePortMismatch = (userCruise.departurePort || '').trim().toLowerCase() !== 
            (officialItinerary.departurePort || '').trim().toLowerCase();
            
          const returnDateMismatch = userCruise.returnDate !== officialItinerary.returnDate;

          // Compare ports
          const userPorts = userCruise.ports || [];
          const officialPorts = officialItinerary.ports || [];
          
          let portsMismatch = userPorts.length !== officialPorts.length;
          let customExcursionsCount = 0;
          let customPortsCount = 0;
          const matchedIndices = new Set<number>();

          // Check for individual port details
          officialPorts.forEach(op => {
            // Find best user port by index to be immune to duplicate port IDs
            const exactMatchIdx = userPorts.findIndex((p, idx) => 
              p.date === op.date && 
              p.name.trim().toLowerCase() === op.name.trim().toLowerCase() &&
              !matchedIndices.has(idx)
            );
            
            const fallbackIdx = userPorts.findIndex((p, idx) => 
              p.date === op.date && 
              !matchedIndices.has(idx)
            );
            
            const upIdx = exactMatchIdx !== -1 ? exactMatchIdx : fallbackIdx;

            if (upIdx === -1) {
              portsMismatch = true;
            } else {
              matchedIndices.add(upIdx);
              const up = userPorts[upIdx];
              if (up.name.trim().toLowerCase() !== op.name.trim().toLowerCase() ||
                  (up.arrivalTime || '').trim() !== (op.arrivalTime || '').trim() ||
                  (up.departureTime || '').trim() !== (op.departureTime || '').trim() ||
                  !up.fromItinerary) {
                portsMismatch = true;
              }
              if (up.excursion && up.excursion.trim()) {
                customExcursionsCount++;
              }
            }
          });

          // Check if user added extra custom ports not on official dates
          userPorts.forEach((_, idx) => {
            if (!matchedIndices.has(idx)) {
              customPortsCount++;
              portsMismatch = true;
            }
          });

          const hasCustomExcursions = customExcursionsCount > 0;
          const hasCustomPorts = customPortsCount > 0;
          
          const hasCustomBookingInfo = !!(
            userCruise.cabinNumber ||
            userCruise.bookingNumber ||
            userCruise.notes ||
            userCruise.cruiseCost ||
            userCruise.diningTime ||
            userCruise.hasDrinkPackage ||
            userCruise.hasInternetPackage
          );

          if (departurePortMismatch || returnDateMismatch || portsMismatch) {
            const userData = usersMap.get(userCruise.userId) || { name: 'Unknown User', email: 'No Email' };
            
            let linkStatus: 'linked' | 'user_unlinked' | 'never_linked' = 'linked';
            if ((userCruise as any).linkBroken) {
              linkStatus = 'user_unlinked';
            } else if (!(userCruise as any).itineraryKey) {
              linkStatus = 'never_linked';
            }

            unalignedList.push({
              userCruise,
              officialItinerary,
              userName: userData.name,
              userEmail: userData.email,
              linkStatus,
              diffs: {
                departurePortMismatch,
                returnDateMismatch,
                portsMismatch,
                hasCustomExcursions,
                customExcursionsCount,
                hasCustomPorts,
                customPortsCount,
                hasCustomBookingInfo
              }
            });
          }
        }
      });

      setUnreconciled(unalignedList);
    } catch (error) {
      console.error('Reconciliation scan error:', error);
      toast.error('Failed to scan itineraries for reconciliation');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    scanCruises();
  }, []);

  const handleAlign = async (item: UnreconciledCruise) => {
    setProcessingId(item.userCruise.id);
    try {
      await alignCruiseToOfficial(item.userCruise.id, item.officialItinerary);
      toast.success(`Successfully aligned cruise for ${item.userName}`);
      // Remove from local list instantly rather than performing a heavy database re-scan
      setUnreconciled(prev => prev.filter(x => x.userCruise.id !== item.userCruise.id));
    } catch (error: any) {
      console.error('Alignment error:', error);
      toast.error(`Alignment failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setProcessingId(null);
    }
  };

  const handleIgnore = async (item: UnreconciledCruise) => {
    if (!window.confirm(`Are you sure you want to ignore reconciliation and keep the custom itinerary for ${item.userName}'s cruise?`)) {
      return;
    }
    setProcessingId(item.userCruise.id);
    try {
      await ignoreCruiseReconciliation(item.userCruise.id);
      toast.success(`Ignored reconciliation for ${item.userName}`);
      // Remove from local list instantly
      setUnreconciled(prev => prev.filter(x => x.userCruise.id !== item.userCruise.id));
    } catch (error: any) {
      console.error('Ignore error:', error);
      toast.error(`Operation failed: ${error?.message || 'Unknown error'}`);
    } finally {
      setProcessingId(null);
    }
  };

  // Filter List
  const filteredList = unreconciled.filter(item => {
    const matchesCruiseLine = !selectedCruiseLine || item.userCruise.cruiseLine === selectedCruiseLine;
    const matchesSearch = item.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.userCruise.shipName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.userEmail.toLowerCase().includes(searchQuery.toLowerCase());
      
    let matchesDiff = true;
    if (diffFilter === 'ports') matchesDiff = item.diffs.portsMismatch;
    if (diffFilter === 'depPort') matchesDiff = item.diffs.departurePortMismatch;
    if (diffFilter === 'retDate') matchesDiff = item.diffs.returnDateMismatch;

    let matchesLink = true;
    if (linkFilter === 'linked') matchesLink = item.linkStatus === 'linked';
    if (linkFilter === 'user_unlinked') matchesLink = item.linkStatus === 'user_unlinked';
    if (linkFilter === 'never_linked') matchesLink = item.linkStatus === 'never_linked';

    return matchesCruiseLine && matchesSearch && matchesDiff && matchesLink;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <GitMerge className="h-8 w-8 text-indigo-600" />
            Cruise Itinerary Reconciliation
          </h1>
          <p className="text-gray-600 mt-1">
            Detect user-logged cruises that differ from official itineraries and align them without losing custom fields.
          </p>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Cruise Line Filter */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Filter Cruise Line</label>
            <select
              value={selectedCruiseLine}
              onChange={(e) => setSelectedCruiseLine(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm bg-white focus:border-indigo-500 focus:outline-none"
            >
              <option value="">All Cruise Lines</option>
              {cruiseLines.map(cl => (
                <option key={cl.id} value={cl.name}>{cl.name}</option>
              ))}
            </select>
          </div>

          {/* Search Query */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Search User or Ship</label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search name, ship..."
                className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Reload / Scan */}
          <div className="flex items-end justify-end">
            <button
              onClick={scanCruises}
              disabled={loading}
              className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 disabled:opacity-50 transition-colors w-full md:w-auto justify-center"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Rescan
            </button>
          </div>
        </div>

        {/* Link Status & Diff filters */}
        <div className="space-y-3">
          <div className="flex border-b border-gray-100 gap-6 text-sm flex-wrap">
            {(['all', 'linked', 'user_unlinked', 'never_linked'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setLinkFilter(tab)}
                className={`pb-2.5 font-semibold capitalize border-b-2 transition-colors ${
                  linkFilter === tab
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'all' && `All (${filteredList.length})`}
                {tab === 'linked' && 'Linked (Out of Sync)'}
                {tab === 'user_unlinked' && 'User Unlinked'}
                {tab === 'never_linked' && 'Legacy / Never Linked'}
              </button>
            ))}
          </div>

          <div className="flex gap-4 text-xs text-gray-500">
            {(['all', 'ports', 'depPort', 'retDate'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setDiffFilter(tab)}
                className={`px-2.5 py-1 rounded-full font-medium transition-colors ${
                  diffFilter === tab
                    ? 'bg-indigo-100 text-indigo-800 font-semibold'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {tab === 'all' && 'All Field Diffs'}
                {tab === 'ports' && 'Port Mismatch'}
                {tab === 'depPort' && 'Departure Port Mismatch'}
                {tab === 'retDate' && 'Return Date Mismatch'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main List */}
      {loading ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
            <p className="text-gray-500 font-medium">Scanning user cruises for misalignments...</p>
          </div>
        </div>
      ) : filteredList.length === 0 ? (
        <div className="p-16 text-center text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col items-center">
          <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-2" />
          <p className="text-lg font-semibold text-gray-800">All cruises are perfectly aligned!</p>
          <p className="text-sm text-gray-500 mt-1">
            No logged user cruises match the selected filter criteria.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredList.map(item => (
            <div 
              key={item.userCruise.id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col justify-between"
            >
              {/* Card Header */}
              <div className="bg-gray-50/50 p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{item.userCruise.shipName}</h3>
                    <span className="text-xs font-semibold bg-gray-100 border border-gray-200 text-gray-700 px-2 py-0.5 rounded">
                      {item.userCruise.cruiseLine}
                    </span>
                    {item.linkStatus === 'linked' && (
                      <span className="text-xs font-semibold bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                        Linked (Out of Sync)
                      </span>
                    )}
                    {item.linkStatus === 'user_unlinked' && (
                      <span className="text-xs font-semibold bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded" title="User clicked Break Link to manage schedule manually">
                        User Intentionally Unlinked
                      </span>
                    )}
                    {item.linkStatus === 'never_linked' && (
                      <span className="text-xs font-semibold bg-gray-50 border border-gray-200 text-gray-600 px-2 py-0.5 rounded">
                        Legacy / Never Linked
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1 flex-wrap">
                    <span className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5 text-gray-400" />
                      {item.userName} ({item.userEmail})
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      Sailing: {item.userCruise.departureDate}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-stretch sm:self-auto flex-wrap">
                  <button
                    onClick={() => handleIgnore(item)}
                    disabled={processingId !== null}
                    className="rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2 shadow-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    Ignore & Keep Custom
                  </button>
                  <button
                    onClick={() => handleAlign(item)}
                    disabled={processingId !== null}
                    className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 shadow-sm disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
                  >
                    {processingId === item.userCruise.id ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Aligning...
                      </>
                    ) : (
                      <>
                        <GitMerge className="h-4 w-4" />
                        Align to Official
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Diffs Side by Side comparison */}
              <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-gray-100">
                {/* User Data */}
                <div className="space-y-4">
                  <h4 className="font-bold text-xs uppercase text-gray-500 tracking-wider flex items-center gap-1">
                    <User className="h-4 w-4 text-gray-400" />
                    User's Cruise Version
                  </h4>
                  
                  <div className="bg-orange-50/30 rounded-lg p-3 border border-orange-100 text-sm space-y-2">
                    <div>
                      <span className="font-semibold text-gray-600 block text-xs">Departure Port</span>
                      <span className={item.diffs.departurePortMismatch ? 'text-orange-700 font-semibold bg-orange-100/60 px-1 rounded' : 'text-gray-800'}>
                        {item.userCruise.departurePort || 'None Specified'}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-600 block text-xs">Return Date</span>
                      <span className={item.diffs.returnDateMismatch ? 'text-orange-700 font-semibold bg-orange-100/60 px-1 rounded' : 'text-gray-800'}>
                        {item.userCruise.returnDate}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="font-bold text-xs text-gray-600 block mb-2">Ports List ({item.userCruise.ports?.length || 0})</span>
                    <div className="space-y-1 max-h-56 overflow-y-auto border border-gray-100 rounded-md bg-gray-50/20 p-2 font-mono text-xs">
                      {item.userCruise.ports?.map((port, pIdx) => {
                        // Check if it matches official on same date
                        const opMatch = item.officialItinerary.ports.find(op => op.date === port.date);
                        const isMismatch = !opMatch || opMatch.name.trim().toLowerCase() !== port.name.trim().toLowerCase();
                        
                        return (
                          <div 
                            key={`${port.id || 'port'}_${pIdx}`} 
                            className={`p-1.5 rounded flex justify-between ${
                              isMismatch ? 'bg-orange-50 text-orange-800 border-l-2 border-orange-500' : 'text-gray-700'
                            }`}
                          >
                            <div>
                              <span className="font-semibold">{port.date}</span>: {port.name}
                              {port.excursion && (
                                <span className="block text-[10px] text-indigo-600 font-sans italic mt-0.5">
                                  Excursion: {port.excursion}
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] opacity-75 font-sans">
                              {port.arrivalTime || '--'} - {port.departureTime || '--'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Official Itinerary Data */}
                <div className="space-y-4">
                  <h4 className="font-bold text-xs uppercase text-gray-500 tracking-wider flex items-center gap-1">
                    <Anchor className="h-4 w-4 text-emerald-500" />
                    Official Itinerary Version
                  </h4>

                  <div className="bg-emerald-50/30 rounded-lg p-3 border border-emerald-100 text-sm space-y-2">
                    <div>
                      <span className="font-semibold text-gray-600 block text-xs">Departure Port</span>
                      <span className={item.diffs.departurePortMismatch ? 'text-emerald-700 font-semibold bg-emerald-100/60 px-1 rounded' : 'text-gray-800'}>
                        {item.officialItinerary.departurePort}
                      </span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-600 block text-xs">Return Date</span>
                      <span className={item.diffs.returnDateMismatch ? 'text-emerald-700 font-semibold bg-emerald-100/60 px-1 rounded' : 'text-gray-800'}>
                        {item.officialItinerary.returnDate}
                      </span>
                    </div>
                  </div>

                  <div>
                    <span className="font-bold text-xs text-gray-600 block mb-2">Ports List ({item.officialItinerary.ports.length})</span>
                    <div className="space-y-1 max-h-56 overflow-y-auto border border-gray-100 rounded-md bg-gray-50/20 p-2 font-mono text-xs">
                      {item.officialItinerary.ports.map((port, pIdx) => {
                        const upMatch = item.userCruise.ports?.find(up => up.date === port.date);
                        const isMismatch = !upMatch || upMatch.name.trim().toLowerCase() !== port.name.trim().toLowerCase();

                        return (
                          <div 
                            key={pIdx} 
                            className={`p-1.5 rounded flex justify-between ${
                              isMismatch ? 'bg-emerald-50 text-emerald-800 border-l-2 border-emerald-500' : 'text-gray-700'
                            }`}
                          >
                            <div>
                              <span className="font-semibold">{port.date}</span>: {port.name}
                            </div>
                            <div className="text-[10px] opacity-75 font-sans">
                              {port.arrivalTime || '--'} - {port.departureTime || '--'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Customizations summary footer */}
              <div className="p-4 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-gray-600 gap-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
                  <span className="font-semibold text-gray-700">User Customizations Preserved:</span>
                  
                  {item.diffs.hasCustomExcursions && (
                    <span className="inline-flex items-center gap-1 rounded bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 font-medium">
                      {item.diffs.customExcursionsCount} Excursion(s)
                    </span>
                  )}
                  
                  {item.diffs.hasCustomPorts && (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-100 text-amber-700 px-2 py-0.5 font-medium">
                      {item.diffs.customPortsCount} Custom Port(s)
                    </span>
                  )}

                  {item.diffs.hasCustomBookingInfo && (
                    <span className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-100 text-blue-700 px-2 py-0.5 font-medium">
                      Cabin / Booking Details / Notes
                    </span>
                  )}

                  {!item.diffs.hasCustomExcursions && !item.diffs.hasCustomPorts && !item.diffs.hasCustomBookingInfo && (
                    <span className="text-gray-400 italic">None</span>
                  )}
                </div>

                <div className="flex items-center gap-1 text-[11px] text-gray-400 italic font-mono">
                  Cruise ID: {item.userCruise.id}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default AdminItineraryReconciliation;
